import React, {useCallback, useEffect, useState} from 'react';
import {
  Grid,
  Typography,
  Tooltip,
  Card,
  CardContent,
  Box,
  Button,
  useMediaQuery,
  useTheme,
  Chip
} from '@mui/material';
import {CardMediaComponent} from '@/components/ui/card-media';
import {GroupData} from '@/types/group';
import {Sport} from '@/types/common';
import {
  getCompanyLabel,
  isDocumentCompanyRoleHealthManager,
  isUserDocumentManager,
  useTitleStyle
} from '@/utils/';
import {useTranslation} from 'react-i18next';
import {deleteGroup} from '@/rest/groups/delete-group';
import {useAppSelector} from '@/redux-store/store';
import ConfirmationDialog from '@/components/ui/dialogs/confirmation-dialog';
import {useSnackbar} from '@/contexts/snackbar';
import DialogForm from '@/components/ui/dialogs/form-dialog';
import GroupEditForm from '@/components/forms/groups/update';
import {getGroupById} from '@/rest/groups/get-group';
import {env} from '@/config';
import {useCompanyContext} from '@/contexts/admin/companies';
import {useMatrixUnreadEventsContext} from '@/contexts/matrix/unread-events';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';

const {CONTENT_SERVER, APP_VARIANT} = env;
interface GroupCardProps {
  group: GroupData;
  onGroupCardClick: (group: GroupData) => void;
  onChange?: (group: GroupData) => void;
  onDelete?: (groupId: string) => void;
}

const groupsImage = `${CONTENT_SERVER}assets/sports/team.jpg`;

const getImageSource = (group: GroupData, sport: Sport | null): string => {
  if (group.picture) {
    return `${group.picture}?timestamp=${group.updatedAt}`;
  }
  if (group.sports && group.sports.length > 1) {
    return groupsImage;
  }
  return sport?.picture || groupsImage;
};

const GroupCard: React.FC<GroupCardProps> = ({
  group,
  onGroupCardClick,
  onChange = () => {
    // noop
  },
  onDelete = () => {
    // noop
  }
}) => {
  const {t, i18n} = useTranslation();

  const {showSnackbarMessage} = useSnackbar();
  const {sports} = useAppSelector((state) => state.sports);
  const userInfo = useAppSelector((state) => state.userInfos.userInfos);
  const userRole = useAppSelector((state) => state.auth.keycloakUser?.userRole);
  const userCompanies = useAppSelector(
    (state) => state.userInfos.userInfos.companies
  );
  const selectedGroupFromRedux = useAppSelector((state) =>
    state.groups.myGroups.find((groupToFind) => groupToFind._id === group._id)
  );
  const {isUserHealthManager} = useAppSelector((state) => state.auth);
  const {refreshCompanyData} = useCompanyContext() || {};

  const {unreadEventsCount} = useMatrixUnreadEventsContext();

  const [showGroupEditForm, setShowGroupEditForm] = useState(false);
  const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
  const [selectedGroupForEdit, setSelectedGroupForEdit] =
    useState<GroupData | null>(null);
  const [selectedGroupToUse, setSelectedGroupToUse] = useState<
    GroupData | undefined
  >(group);
  const [showMainTooltip, setShowMainTooltip] = useState(false);

  const isUserTrainer =
    userRole && userRole === 'TRAINER' && APP_VARIANT === 'sjone';

  const isUserManager = isUserDocumentManager(group, userInfo);

  const isHealthManagerGroup =
    group && isDocumentCompanyRoleHealthManager(group);

  const companyGroupAndUserHealthManager =
    isUserHealthManager && isHealthManagerGroup;

  const handleEditButtonClick = useCallback((group: GroupData) => {
    setSelectedGroupForEdit(group);
    setShowGroupEditForm(true);
  }, []);

  const onGroupEdited = useCallback(async () => {
    showSnackbarMessage(t('group_update_success'), 'success');

    const response = await getGroupById(group._id);

    onChange(response);

    setShowGroupEditForm(false);
  }, [group._id, onChange, showSnackbarMessage, t]);

  const onGroupEditError = useCallback(() => {
    showSnackbarMessage(t('group_update_failure'), 'error');
  }, [showSnackbarMessage, t]);

  const handleGroupEditCancel = useCallback(() => {
    setShowGroupEditForm(false);
  }, []);

  const handleDeleteClick = useCallback(() => {
    if (group) {
      const groupId = group._id;

      deleteGroup(groupId)
        .then((response) => {
          if (response) {
            showSnackbarMessage(t('group_deleting_success'), 'success');
            refreshCompanyData();
            onDelete(groupId);
          } else {
            showSnackbarMessage(t('group_deleting_failure'), 'error');
          }
        })
        .catch((error) => {
          console.error('Error deleting event:', error);
          showSnackbarMessage(t('group_deleting_failure'), 'error');
        })
        .finally(() => {
          setDeleteConfirmationOpen(false);
        });
    }
  }, [group, onDelete, showSnackbarMessage, t, refreshCompanyData]);

  const handleDeleteConfirmation = useCallback(
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e.stopPropagation();
      setDeleteConfirmationOpen(true);
    },
    []
  );

  const handleCloseConfirmationDialog = useCallback(() => {
    setDeleteConfirmationOpen(false);
  }, []);

  const handleButtonMouseEnter = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const handleButtonMouseLeave = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseEnter = useCallback(() => {
    setShowMainTooltip(true);
  }, []);

  const handleCardMouseLeave = useCallback(() => {
    setShowMainTooltip(false);
  }, []);

  const titleStyle = useTitleStyle('17ch', undefined, undefined, false, 'md');
  const companyNameStyle = useTitleStyle(
    '30ch',
    undefined,
    undefined,
    false,
    'md'
  );
  const theme = useTheme();
  const isXsScreen = useMediaQuery(theme.breakpoints.down('xs'));

  let sport = null;
  if (group.sports && group.sports.length > 0) {
    sport = sports[group.sports[0]._id];
    if (!sport) {
      console.warn(`No sport found with id ${group.sports[0]._id}`);
    }
  }

  const sportLabel =
    i18n.language === 'en' ? sport?.labels.en : sport?.labels.de;

  const imageSource = getImageSource(group, sport);

  useEffect(() => {
    if (selectedGroupFromRedux) {
      setSelectedGroupToUse(selectedGroupFromRedux);
    } else {
      setSelectedGroupToUse(group);
    }
  }, [selectedGroupFromRedux, group]);

  return selectedGroupToUse ? (
    <Grid item xs={12} sm={12} md={12} lg={12}>
      <Tooltip title={t('open_group')} open={showMainTooltip}>
        <Card
          onMouseEnter={handleCardMouseEnter}
          onMouseLeave={handleCardMouseLeave}
          sx={{
            width: '100%',
            cursor: 'pointer',
            display: 'flex',
            flexDirection: {xs: 'column', md: 'row'}
          }}
          onClick={() => {
            onGroupCardClick(group);
          }}
        >
          <Grid container alignItems={isXsScreen ? 'flex-start' : 'center'}>
            <Grid item xs={12} sm={12} md={4} lg={4}>
              <CardMediaComponent
                height={150}
                image={imageSource}
                altText={sportLabel || ''}
                sport={sportLabel || ''}
                sx={{
                  boxShadow: 0
                }}
              />
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={4}>
              <CardContent
                sx={{
                  paddingLeft: '30px'
                }}
              >
                <Box
                  display="flex"
                  flexDirection="column"
                  justifyContent="flex-start"
                  alignItems={'flex-start'}
                  flexGrow={1}
                >
                  {isUserTrainer && group.company && (
                    <Typography
                      gutterBottom
                      variant="subtitle2"
                      component="div"
                      sx={companyNameStyle}
                    >
                      {getCompanyLabel(group.company, userCompanies || []) ||
                        'company'}
                    </Typography>
                  )}
                  <Box display="flex" alignItems="center">
                    <Typography
                      gutterBottom
                      variant="h6"
                      component="div"
                      sx={titleStyle}
                    >
                      {selectedGroupToUse.title}
                    </Typography>
                    {unreadEventsCount > 0 && (
                      <Tooltip
                        title={t('unread_events_count')}
                        onMouseEnter={handleButtonMouseEnter}
                        onMouseLeave={handleButtonMouseLeave}
                      >
                        <Chip
                          sx={{marginBottom: 0.5, marginLeft: 1}}
                          size="small"
                          icon={<NotificationsActiveIcon />}
                          label={unreadEventsCount}
                          color="secondary"
                        />
                      </Tooltip>
                    )}
                    {selectedGroupToUse.trainerGroup && (
                      <Tooltip
                        title={t('trainer_group')}
                        placement="top"
                        onMouseEnter={handleButtonMouseEnter}
                        onMouseLeave={handleButtonMouseLeave}
                      >
                        <SportsBasketballIcon
                          color="secondary"
                          sx={{
                            marginBottom: 0.5,
                            marginLeft: 1
                          }}
                        />
                      </Tooltip>
                    )}
                  </Box>
                  <Typography variant="body2">
                    {t('group_members')}:{' '}
                    {selectedGroupToUse.members
                      ? selectedGroupToUse.members.length
                      : 0}
                  </Typography>

                  <Typography variant="body2" marginTop={1}>
                    {t('events_screen')}:{' '}
                    {selectedGroupToUse.events
                      ? selectedGroupToUse.events.length
                      : 0}
                  </Typography>
                </Box>
              </CardContent>
            </Grid>

            <Grid item xs={12} sm={12} md={4} lg={4}>
              {(isUserManager || companyGroupAndUserHealthManager) && (
                <Box
                  display="flex"
                  justifyContent={'center'}
                  alignItems="center"
                  sx={{
                    marginRight: {xs: 0, md: '20px'},
                    marginBottom: {xs: '20px', md: 0}
                  }}
                >
                  <Tooltip title={t('edit_group')}>
                    <Button
                      onMouseEnter={handleButtonMouseEnter}
                      onMouseLeave={handleButtonMouseLeave}
                      variant="contained"
                      color="primary"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleEditButtonClick(group);
                      }}
                      sx={{
                        marginRight: '30px',
                        minWidth: '90px',
                        width: {xs: '115px'}
                      }}
                    >
                      {t('edit')}
                    </Button>
                  </Tooltip>
                  <Tooltip title={t('delete_group')}>
                    <Button
                      onMouseEnter={handleButtonMouseEnter}
                      onMouseLeave={handleButtonMouseLeave}
                      variant="contained"
                      color="primary"
                      onClick={handleDeleteConfirmation}
                      sx={{
                        minWidth: '90px',
                        width: {xs: '115px'}
                      }}
                    >
                      {t('delete')}
                    </Button>
                  </Tooltip>
                </Box>
              )}
            </Grid>
          </Grid>
        </Card>
      </Tooltip>
      <ConfirmationDialog
        open={deleteConfirmationOpen}
        title={t('delete_group_dialog_title')}
        message={t('delete_group_dialog_content')}
        onConfirm={handleDeleteClick}
        onCancel={handleCloseConfirmationDialog}
        warn
      />

      {selectedGroupForEdit && (
        <DialogForm
          open={showGroupEditForm}
          onCancel={handleGroupEditCancel}
          onSubmit={onGroupEdited}
          onError={onGroupEditError}
          FormComponent={GroupEditForm}
          initialValues={selectedGroupForEdit}
          maxHeightAllowed="1200px"
        />
      )}
    </Grid>
  ) : null;
};

export default GroupCard;
